// ProposalSmith engine — win-tested proposal templates with 3-option pricing.

export interface PricingOption {
  id: string;
  name: string;             // e.g. "Essentials", "Growth", "Scale"
  price: number;
  deliverables: string[];
  highlight?: boolean;      // mark recommended
}

export interface Proposal {
  id: string;
  title: string;
  date: string;
  clientName: string;
  clientCompany: string;
  problemFraming: string;
  approach: string;
  socialProof: string;
  options: PricingOption[];
  whatHappensIfNothing: string;
  whyMe: string;
  nextStep: string;
  validUntil: string;
  notes: string;
  status: 'draft' | 'sent' | 'accepted' | 'declined';
  acceptedOptionId?: string;
}

export const WIN_TEMPLATES = {
  consulting: {
    problemFraming: `You're losing [hours/dollars/customers] every [day/week/month] because [specific issue]. Left unaddressed, this becomes [downstream cost / risk]. You've tried [X / Y / Z]; results have been [outcome]. The pattern I see in similar engagements is that this is [root cause], not [symptom].`,
    approach: `**Phase 1 — Discovery (Week 1):** Stakeholder interviews, data audit, problem confirmation. Deliverable: a tight problem statement we both agree on.\n\n**Phase 2 — Design (Weeks 2-3):** Solution options with trade-offs, recommended path, success metrics.\n\n**Phase 3 — Build & Ship (Weeks 4-N):** Iterative delivery against milestones with weekly progress updates.\n\n**Phase 4 — Handover (Final week):** Documentation, training, transition plan.`,
    whyMe: `Over the last [X years], I've helped [N] organizations like yours solve this exact class of problem. I bring a defined methodology, not a "let's see how it goes" approach. My engagements come with: weekly written progress, fixed-price options, and a no-questions money-back guarantee on Phase 1 if the problem framing doesn't resonate.`,
  },
  productdev: {
    problemFraming: `Your team is shipping features but [conversion / retention / activation] isn't improving. The data points to [specific drop-off / friction point]. Most teams attempt to fix this with [common but wrong move]; that creates [unintended consequence].`,
    approach: `**Sprint 0:** Funnel audit + cohort analysis. Identify the highest-leverage drop-off.\n\n**Sprints 1-3:** Ship 1-2 experiments per sprint targeting that drop-off. Each ships with success criteria defined upfront.\n\n**Sprint 4:** Synthesis. What worked, what didn't, what to build next.`,
    whyMe: `I've run this exact playbook on [N] products. The 90-day result is typically [X-Y%] lift on the targeted metric. You get: experiment ownership, weekly readouts, and a permanent improvement to your team's experimentation muscle.`,
  },
  retainer: {
    problemFraming: `You need an experienced [BA / PM / consultant] embedded with your team but a full-time hire is [too expensive / too slow to recruit / wrong commitment shape]. A traditional agency would over-staff and under-deliver. You need a senior individual on a defined cadence.`,
    approach: `Monthly retainer: [X] hours of senior advisory time, billed monthly. Weekly check-in, ongoing access via Slack/email, and rapid response on critical asks. Engagement scoped in 3-month renewals.`,
    whyMe: `My retainer clients stay an average of [X] months. They get a senior advisor for ~1/3 the cost of a full hire, with none of the recruiting overhead. Boundary: this is not body-shop labor — it's high-leverage thinking time.`,
  },
};

export function emptyProposal(): Proposal {
  const valid = new Date(Date.now() + 30 * 86400_000).toISOString().slice(0, 10);
  return {
    id: crypto.randomUUID(),
    title: '', date: new Date().toISOString().slice(0, 10),
    clientName: '', clientCompany: '',
    problemFraming: '', approach: '', socialProof: '',
    options: [
      { id: crypto.randomUUID(), name: 'Essentials', price: 5000, deliverables: [] },
      { id: crypto.randomUUID(), name: 'Growth', price: 12000, deliverables: [], highlight: true },
      { id: crypto.randomUUID(), name: 'Scale', price: 25000, deliverables: [] },
    ],
    whatHappensIfNothing: '', whyMe: '', nextStep: 'Schedule a 30-min kickoff call.',
    validUntil: valid, notes: '', status: 'draft',
  };
}

export function applyTemplate(p: Proposal, key: keyof typeof WIN_TEMPLATES): Proposal {
  const tpl = WIN_TEMPLATES[key];
  return { ...p, problemFraming: tpl.problemFraming, approach: tpl.approach, whyMe: tpl.whyMe };
}

export function generateProposalMarkdown(p: Proposal): string {
  return `# ${p.title || 'Proposal'}

**For:** ${p.clientName}, ${p.clientCompany}
**From:** _(your name)_
**Date:** ${p.date}
**Valid until:** ${p.validUntil}

## The problem

${p.problemFraming || '_(not filled)_'}

## What happens if nothing changes

${p.whatHappensIfNothing || '_(not filled)_'}

## My approach

${p.approach || '_(not filled)_'}

## Why me

${p.whyMe || '_(not filled)_'}

${p.socialProof ? `## Social proof\n\n${p.socialProof}\n` : ''}

## Pricing options

${p.options.map(o => `### ${o.name}${o.highlight ? ' ⭐ recommended' : ''}\n\n**$${o.price.toLocaleString()}**\n\n${o.deliverables.length ? o.deliverables.map(d => `- ${d}`).join('\n') : '_(deliverables to be confirmed)_'}\n`).join('\n')}

## Next step

${p.nextStep}

${p.notes ? `\n---\n\n${p.notes}\n` : ''}
`;
}

export function generateProposalHTML(p: Proposal): string {
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${p.title}</title>
<style>body{font-family:-apple-system,sans-serif;color:#0a1628;max-width:780px;margin:40px auto;padding:32px;background:#fff;line-height:1.6}h1{color:#0a1628;border-bottom:3px solid #d4af37;padding-bottom:12px}h2{color:#0a1628;margin-top:32px}.options{display:grid;grid-template-columns:repeat(${p.options.length},1fr);gap:16px;margin:24px 0}.opt{padding:20px;border:2px solid #e5e7eb;border-radius:12px}.opt.h{border-color:#d4af37;background:linear-gradient(135deg,#fefce8 0%,#fff 100%);position:relative}.opt.h::before{content:'⭐ Recommended';position:absolute;top:-12px;left:20px;background:#d4af37;color:#0a1628;padding:4px 12px;border-radius:9999px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em}.opt h3{margin:0 0 8px;color:#0a1628}.price{font-size:32px;font-weight:700;color:#d4af37;margin:8px 0}.next{margin-top:32px;padding:20px;background:#0a1628;color:#f0f0f0;border-radius:12px;text-align:center}</style></head>
<body><h1>${p.title || 'Proposal'}</h1>
<p><strong>For:</strong> ${p.clientName}, ${p.clientCompany}<br/><strong>Date:</strong> ${p.date} · <strong>Valid until:</strong> ${p.validUntil}</p>
<h2>The problem</h2><p>${p.problemFraming.replace(/\n/g, '<br/>')}</p>
${p.whatHappensIfNothing ? `<h2>What happens if nothing changes</h2><p>${p.whatHappensIfNothing.replace(/\n/g, '<br/>')}</p>` : ''}
<h2>My approach</h2><p>${p.approach.replace(/\n/g, '<br/>')}</p>
<h2>Why me</h2><p>${p.whyMe.replace(/\n/g, '<br/>')}</p>
${p.socialProof ? `<h2>Social proof</h2><p>${p.socialProof.replace(/\n/g, '<br/>')}</p>` : ''}
<h2>Pricing options</h2>
<div class="options">${p.options.map(o => `<div class="opt ${o.highlight ? 'h' : ''}"><h3>${o.name}</h3><div class="price">$${o.price.toLocaleString()}</div><ul>${o.deliverables.map(d => `<li>${d}</li>`).join('')}</ul></div>`).join('')}</div>
<div class="next"><strong>Next step:</strong> ${p.nextStep}</div>
</body></html>`;
}
